import React, { useEffect, useRef, useState } from "react";
import ToggleBtn from "./ToggleBtn";
import mainContentData from "./mainContent.json";
import houseKeepingData from "./houseKeeping.json";
import CabData from "./CabData.json";
import axiosInstance from "../../../api/axiosInstance";
import Checkin from "./CheckIn-CheckOut.json";
import PageLoader from "../../loader/PageLoader";
import Restaurant from "./Restaurant";
import { toast } from "react-toastify";
import { GST_OPTIONS } from "../../../api/gst";

// The sub-services each parent menu owns. Closing a sub-menu without saving restores
// only these entries, so a parent toggle the guest flipped stays flipped.
const SUBMENU_SERVICE_NAMES = {
    HouseKeeping: houseKeepingData.map((data) => data.name),
    Cab: CabData.map((data) => data.name),
    "Check In - Check out": Checkin.map((data) => data.name),
};

// Both service endpoints reject an empty list, so the cases that would send one are
// caught here and explained, rather than surfacing as a bare "non zero value required".
const validationMessage = (services, housekeeping) => {
    if (services.length === 0) {
        return "Enable at least one service before saving";
    }

    const housekeepingEnabled = services.some(
        (service) => service.name === "HouseKeeping",
    );
    if (housekeepingEnabled && housekeeping.length === 0) {
        return "Select at least one housekeeping service, or turn Housekeeping off";
    }

    return "";
};

const Service = ({ onComplete }) => {
    // const [service, setService] = useState("HouseKeeping");
    const [servicesArray, setServicesArray] = useState([]);
    const [housekeepingArray, setHousekeepingArray] = useState([]);
    const hotelId = localStorage.getItem("hotelId");
    const [fetchedServices, setFetchedServices] = useState([]);
    const [isHousekeepingEnabled, setIsHousekeepingEnabled] = useState(false);
    const [fetchedHousekeepingServices, setFetchedHousekeepingServices] =
        useState([]);
    const [isPageLoading, setIsPageLoading] = useState(null);
    const [activeSubmenu, setActiveSubmenu] = useState(null);
    const [cabToggle, setCabToggle] = useState(false);
    const [checkInOutToggle, setCheckInOutToggle] = useState(false);
    const [earlyCheckinRate, setEarlyCheckinRate] = useState("");
    const [earlyCheckinMaxHours, setEarlyCheckinMaxHours] = useState("");
    const [earlyCheckinGST, setEarlyCheckinGST] = useState("0");
    const [earlyCheckinError, setEarlyCheckinError] = useState("");
    const [lateCheckoutRate, setLateCheckoutRate] = useState("");
    const [lateCheckoutMaxHours, setLateCheckoutMaxHours] = useState("");
    const [lateCheckoutGST, setLateCheckoutGST] = useState("0");
    const [lateCheckoutError, setLateCheckoutError] = useState("");

    // What the server currently holds. Sub-menu edits are reverted to this when the
    // panel is closed without saving, so nothing inside a sub-menu reaches the server
    // without an explicit Save.
    const savedState = useRef({
        services: [],
        housekeeping: [],
        earlyCheckinRate: "",
        earlyCheckinMaxHours: "",
        earlyCheckinGST: "0",
        lateCheckoutRate: "",
        lateCheckoutMaxHours: "",
        lateCheckoutGST: "0",
    });

    useEffect(() => {
        const asText = (value) =>
            value === null || value === undefined ? "" : String(value);

        const fetchEarlyCheckinConfig = async () => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/early-checkin-info`,
                );
                const rate = asText(response.data?.early_checkin_hourly_rate);
                const maxHours = asText(response.data?.early_checkin_max_hours);
                const gst = asText(response.data?.early_checkin_gst_percentage ?? 0);
                setEarlyCheckinRate(rate);
                setEarlyCheckinMaxHours(maxHours);
                setEarlyCheckinGST(gst);
                savedState.current.earlyCheckinRate = rate;
                savedState.current.earlyCheckinMaxHours = maxHours;
                savedState.current.earlyCheckinGST = gst;
            } catch (error) {
                console.error("Error fetching early check-in config:", error);
            }
        };

        const fetchLateCheckoutConfig = async () => {
            try {
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/late-checkout-info`,
                );
                const rate = asText(response.data?.late_checkout_hourly_rate);
                const maxHours = asText(response.data?.late_checkout_max_hours);
                const gst = asText(response.data?.late_checkout_gst_percentage ?? 0);
                setLateCheckoutRate(rate);
                setLateCheckoutMaxHours(maxHours);
                setLateCheckoutGST(gst);
                savedState.current.lateCheckoutRate = rate;
                savedState.current.lateCheckoutMaxHours = maxHours;
                savedState.current.lateCheckoutGST = gst;
            } catch (error) {
                console.error("Error fetching late checkout config:", error);
            }
        };

        fetchEarlyCheckinConfig();
        fetchLateCheckoutConfig();
    }, [hotelId]);

    useEffect(() => {
        setIsPageLoading(true);
        const fetchServices = async () => {
            try {
                setIsPageLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/services`,
                );
                const fetchedServicesData = response.data.data;
                setFetchedServices(fetchedServicesData);
                // console.log("Services", fetchedServicesData);

                const updatedServicesArray = fetchedServicesData.map(
                    (service) => ({
                        name: service.name,
                        type: service.type,
                    }),
                );
                setServicesArray(updatedServicesArray);
                savedState.current.services = updatedServicesArray;

                // Check if Housekeeping is enabled
                const housekeepingEnabled = updatedServicesArray.some(
                    (service) => service.name === "HouseKeeping",
                );
                setIsHousekeepingEnabled(housekeepingEnabled);

                // Check if Cab services are enabled
                const cabEnabled = updatedServicesArray.some(
                    (service) =>
                        service.name === "PreCheckin Cab Request" ||
                        service.name === "PostCheckin Cab Request",
                );
                setCabToggle(cabEnabled);

                // Check if Check In - Check out services are enabled
                const checkInOutEnabled = updatedServicesArray.some(
                    (service) =>
                        service.name === "PostCheckin ExtendStay Request" ||
                        service.name === "PreCheckin Early Checkin Request" ||
                        service.name === "PostCheckin Late Checkout Request" ||
                        service.name === "Web Checkin",
                );
                setCheckInOutToggle(checkInOutEnabled);
            } catch (error) {
                console.error("Error fetching general services:", error);
            } finally {
                setIsPageLoading(false);
            }
        };
        fetchServices();
        const fetchHousekeepingServices = async () => {
            try {
                setIsPageLoading(true);
                const response = await axiosInstance.get(
                    `/api/v1/hotels/${hotelId}/hotel-housekeeping-services`,
                );
                const fetchedHousekeepingData = response.data.data;
                setFetchedHousekeepingServices(fetchedHousekeepingData);
                // console.log("Hosuekeeping", fetchedHousekeepingData);
                const updatedHousekeepingArray = fetchedHousekeepingData.map(
                    (service) => ({
                        name: service.name,
                        type: service.type,
                    }),
                );
                setHousekeepingArray(updatedHousekeepingArray);
                savedState.current.housekeeping = updatedHousekeepingArray;
            } catch (error) {
                console.error("Error fetching general services:", error);
            } finally {
                setIsPageLoading(false);
            }
        };
        fetchHousekeepingServices();
    }, [hotelId]);

    const handleSave = () => {
        setActiveSubmenu(null); // Hide the submenu
    };

    // Drop the sub-menu's unsaved edits: put its own services back to what the server
    // holds and leave every other service, including the parent toggles, alone.
    // Returns the reverted arrays as well as applying them: state updates are async, so
    // a caller that saves straight afterwards needs the values in hand.
    const revertSubmenu = (submenu) => {
        const ownedNames = SUBMENU_SERVICE_NAMES[submenu];
        if (!ownedNames) {
            return null;
        }

        const services = [
            ...servicesArray.filter(
                (service) => !ownedNames.includes(service.name),
            ),
            ...savedState.current.services.filter((service) =>
                ownedNames.includes(service.name),
            ),
        ];
        const housekeeping =
            submenu === "HouseKeeping"
                ? savedState.current.housekeeping
                : housekeepingArray;

        setServicesArray(services);

        if (submenu === "HouseKeeping") {
            setHousekeepingArray(savedState.current.housekeeping);
        }

        if (submenu === "Check In - Check out") {
            setEarlyCheckinRate(savedState.current.earlyCheckinRate);
            setEarlyCheckinMaxHours(savedState.current.earlyCheckinMaxHours);
            setEarlyCheckinGST(savedState.current.earlyCheckinGST);
            setLateCheckoutRate(savedState.current.lateCheckoutRate);
            setLateCheckoutMaxHours(savedState.current.lateCheckoutMaxHours);
            setLateCheckoutGST(savedState.current.lateCheckoutGST);
            setEarlyCheckinError("");
            setLateCheckoutError("");
        }

        return { services, housekeeping };
    };

    // Writes the services the hotel currently has. Shared by every sub-menu Save and by
    // Next. The create endpoint rejects a hotel that already has services, so the
    // fetched lists are updated here to keep later saves on the update endpoint.
    const persistServices = async (override) => {
        const updatedServicesArray = removeDuplicates(
            override?.services || servicesArray,
        );
        const housekeepingEnabled = updatedServicesArray.some(
            (service) => service.name === "HouseKeeping",
        );
        const updatedHousekeepingArray = housekeepingEnabled
            ? removeDuplicates(override?.housekeeping || housekeepingArray)
            : [];

        const message = validationMessage(
            updatedServicesArray,
            updatedHousekeepingArray,
        );
        if (message) {
            toast.error(message);
            return false;
        }

        setIsHousekeepingEnabled(housekeepingEnabled);
        if (!housekeepingEnabled) {
            setHousekeepingArray(updatedHousekeepingArray);
        }

        if (housekeepingEnabled) {
            const housekeepingUrl = `/api/v1/hotels/${hotelId}/hotel-housekeeping-services`;
            const housekeepingData = { services: updatedHousekeepingArray };

            fetchedHousekeepingServices.length === 0
                ? await axiosInstance.post(housekeepingUrl, housekeepingData)
                : await axiosInstance.put(housekeepingUrl, housekeepingData);

            setFetchedHousekeepingServices(updatedHousekeepingArray);
        }

        const servicesUrl = `/api/v1/hotels/${hotelId}/services-info`;
        const servicesData = { services: updatedServicesArray };

        fetchedServices.length === 0
            ? await axiosInstance.post(servicesUrl, servicesData)
            : await axiosInstance.put(servicesUrl, servicesData);

        setFetchedServices(updatedServicesArray);

        savedState.current.services = updatedServicesArray;
        savedState.current.housekeeping = updatedHousekeepingArray;

        return true;
    };

    // Save on a sub-menu that only owns service toggles.
    const handleSaveSubmenu = async () => {
        try {
            setIsPageLoading(true);
            if (await persistServices()) {
                handleSave();
            }
        } catch (error) {
            console.error("Error saving services:", error);
            toast.error(
                error?.response?.data?.message || "Could not save the services",
            );
        } finally {
            setIsPageLoading(false);
        }
    };

    const isEarlyCheckinEnabled = servicesArray.some(
        (service) => service.name === "PreCheckin Early Checkin Request",
    );

    const isLateCheckoutEnabled = servicesArray.some(
        (service) => service.name === "PostCheckin Late Checkout Request",
    );

    const handleSaveCheckInOut = async () => {
        if (!isEarlyCheckinEnabled && !isLateCheckoutEnabled) {
            await handleSaveSubmenu();
            return;
        }

        const payload = {};

        if (isEarlyCheckinEnabled) {
            const rate = parseInt(earlyCheckinRate, 10);
            const maxHours = parseInt(earlyCheckinMaxHours, 10);

            if (isNaN(rate) || rate < 0) {
                setEarlyCheckinError("Enter a valid hourly rate");
                return;
            }
            if (isNaN(maxHours) || maxHours < 1 || maxHours > 24) {
                setEarlyCheckinError("Max hours must be between 1 and 24");
                return;
            }

            const gst = parseInt(earlyCheckinGST, 10);
            if (isNaN(gst) || !GST_OPTIONS.includes(gst)) {
                setEarlyCheckinError("Select a GST rate");
                return;
            }

            payload.early_checkin_hourly_rate = rate;
            payload.early_checkin_max_hours = maxHours;
            payload.early_checkin_gst_percentage = gst;
        }

        if (isLateCheckoutEnabled) {
            const rate = parseInt(lateCheckoutRate, 10);
            const maxHours = parseInt(lateCheckoutMaxHours, 10);

            if (isNaN(rate) || rate < 0) {
                setLateCheckoutError("Enter a valid hourly rate");
                return;
            }
            if (isNaN(maxHours) || maxHours < 1 || maxHours > 24) {
                setLateCheckoutError("Max hours must be between 1 and 24");
                return;
            }

            const gst = parseInt(lateCheckoutGST, 10);
            if (isNaN(gst) || !GST_OPTIONS.includes(gst)) {
                setLateCheckoutError("Select a GST rate");
                return;
            }

            payload.late_checkout_hourly_rate = rate;
            payload.late_checkout_max_hours = maxHours;
            payload.late_checkout_gst_percentage = gst;
        }

        try {
            setIsPageLoading(true);

            // Store the services first: if that is rejected the rates are left alone,
            // so the panel never ends up half saved.
            if (!(await persistServices())) {
                return;
            }

            await axiosInstance.patch(
                `/api/v1/hotels/${hotelId}/general-info`,
                payload,
            );

            savedState.current.earlyCheckinRate = earlyCheckinRate;
            savedState.current.earlyCheckinMaxHours = earlyCheckinMaxHours;
            savedState.current.earlyCheckinGST = earlyCheckinGST;
            savedState.current.lateCheckoutRate = lateCheckoutRate;
            savedState.current.lateCheckoutMaxHours = lateCheckoutMaxHours;
            savedState.current.lateCheckoutGST = lateCheckoutGST;

            setEarlyCheckinError("");
            setLateCheckoutError("");
            handleSave();
        } catch (error) {
            console.error("Error saving check-in / checkout config:", error);
            const message =
                error?.response?.data?.message ||
                "Could not save the check-in / checkout settings";
            if (isEarlyCheckinEnabled) setEarlyCheckinError(message);
            if (isLateCheckoutEnabled) setLateCheckoutError(message);
        } finally {
            setIsPageLoading(false);
        }
    };

    const handleServiceClick = (serviceName) => {
        // Leaving a sub-menu by any route other than its Save button throws its edits
        // away, so a sub-service is only ever stored once Save was pressed.
        if (activeSubmenu) {
            revertSubmenu(activeSubmenu);
        }

        setActiveSubmenu((prevSubmenu) =>
            prevSubmenu === serviceName ? null : serviceName,
        );
    };

    const handleMainToggle = (serviceName, isOn) => {
        // console.log("Toggle Main Service:", serviceName, "Is On:", isOn);

        // Switching to another service abandons whatever the open sub-menu had pending.
        // Turning this service off is not a revert: clearing its sub-services is the
        // point, and that rides along with the parent toggle on Next.
        if (activeSubmenu && activeSubmenu !== serviceName) {
            revertSubmenu(activeSubmenu);
        }

        if (serviceName === "Cab") {
            setCabToggle(isOn);
            if (!isOn) {
                // Turn off all Cab sub-services when main toggle is turned off
                setServicesArray((prevArray) =>
                    prevArray.filter(
                        (service) =>
                            service.name !== "PreCheckin Cab Request" &&
                            service.name !== "PostCheckin Cab Request",
                    ),
                );
            }
        } else if (serviceName === "Check In - Check out") {
            setCheckInOutToggle(isOn);
            if (!isOn) {
                // Turn off all Check In - Check out sub-services when main toggle is turned off
                setServicesArray((prevArray) =>
                    prevArray.filter(
                        (service) =>
                            service.name !== "PostCheckin ExtendStay Request" &&
                            service.name !==
                                "PreCheckin Early Checkin Request" &&
                            service.name !==
                                "PostCheckin Late Checkout Request" &&
                            service.name !== "Web Checkin" &&
                            service.name !== "Checkout",
                    ),
                );
            }
        } else if (serviceName === "HouseKeeping") {
            updateServicesArray(serviceName, isOn);
            if (!isOn) {
                // Housekeeping keeps its sub-services in their own array, so clearing
                // the parent has to empty that too — otherwise the children stay on.
                setHousekeepingArray([]);
                setServicesArray((prevArray) =>
                    prevArray.filter(
                        (service) =>
                            !SUBMENU_SERVICE_NAMES.HouseKeeping.includes(
                                service.name,
                            ),
                    ),
                );
            }
        } else {
            updateServicesArray(serviceName, isOn);
        }

        if (isOn) {
            setActiveSubmenu(serviceName);
        } else {
            setActiveSubmenu((prev) => (prev === serviceName ? null : prev));
        }
    };

    const handleSubMenuToggle = (serviceName, isOn, parentService) => {
        // console.log(
        //     "Toggle Submenu Item:",
        //     serviceName,
        //     "Is On:",
        //     isOn,
        //     "Parent:",
        //     parentService,
        // );

        if (parentService === "HouseKeeping") {
            updateServicesArray(serviceName, isOn);
            // Also update the housekeepingArray
            setHousekeepingArray((prevArray) => {
                if (isOn) {
                    const housekeepingService = houseKeepingData.find(
                        (data) => data.name === serviceName,
                    );
                    // console.log(
                    //     "Adding housekeeping service:",
                    //     housekeepingService,
                    // );
                    return [
                        ...prevArray,
                        {
                            name: serviceName,
                            type: housekeepingService?.type || "Post check-in",
                            description: housekeepingService?.description,
                        },
                    ];
                } else {
                    return prevArray.filter(
                        (service) => service.name !== serviceName,
                    );
                }
            });
        } else {
            updateServicesArray(serviceName, isOn);
        }

        // Update main toggle state for Cab and Check In - Check out
        if (parentService === "Cab") {
            const otherService =
                serviceName === "PreCheckin Cab Request"
                    ? "PostCheckin Cab Request"
                    : "PreCheckin Cab Request";
            const otherServiceEnabled = servicesArray.some(
                (service) => service.name === otherService,
            );
            setCabToggle(isOn || otherServiceEnabled);
        } else if (parentService === "Check In - Check out") {
            const otherServices = [
                "PostCheckin ExtendStay Request",
                "PreCheckin Early Checkin Request",
                "PostCheckin Late Checkout Request",
                "Web Checkin",
                "Checkout",
            ];
            const anyServiceEnabled = otherServices.some((service) =>
                service === serviceName
                    ? isOn
                    : servicesArray.some((s) => s.name === service),
            );
            setCheckInOutToggle(anyServiceEnabled);
        }

        // console.log("Updated housekeepingArray:", housekeepingArray);
        // console.log("Updated servicesArray:", servicesArray);
    };

    const handleToggle = (serviceName, isOn, serviceTitle) => {
        // console.log("Toggle Service:", serviceName, "Is On:", isOn);
        updateServicesArray(serviceName, isOn);

        if (isOn) {
            setActiveSubmenu(serviceTitle);
        }
        // Removed the else clause that was closing the submenu
    };

    const updateServicesArray = (serviceName, isOn) => {
        const serviceData =
            mainContentData.find((data) => data.name === serviceName) ||
            CabData.find((data) => data.name === serviceName) ||
            Checkin.find((data) => data.name === serviceName) ||
            houseKeepingData.find((data) => data.title === serviceName);

        if (!serviceData || !serviceData.type) {
            return;
        }

        if (serviceName === "HouseKeeping") {
            setServicesArray((prevArray) => {
                if (isOn) {
                    return [
                        ...prevArray,
                        { name: "HouseKeeping", type: "Post check-in" },
                    ];
                } else {
                    return prevArray.filter(
                        (service) => service.name !== "HouseKeeping",
                    );
                }
            });
            setIsHousekeepingEnabled(isOn);
        } else {
            const isHousekeeping = houseKeepingData.some(
                (data) => data.name === serviceName,
            );

            if (isHousekeeping) {
                setHousekeepingArray((prevArray) => {
                    if (isOn) {
                        const housekeepingService = houseKeepingData.find(
                            (data) => data.name === serviceName,
                        );
                        // console.log(
                        //     "housekeeping service ",
                        //     housekeepingService,
                        // );
                        return [
                            ...prevArray,
                            {
                                name: serviceName,
                                type: housekeepingService.type,
                                description: housekeepingService.description,
                            },
                        ];
                    } else {
                        return prevArray.filter(
                            (service) => service.name !== serviceName,
                        );
                    }
                });
            } else {
                setServicesArray((prevArray) => {
                    if (isOn) {
                        const serviceData =
                            mainContentData.find(
                                (data) => data.name === serviceName,
                            ) ||
                            CabData.find((data) => data.name === serviceName) ||
                            Checkin.find((data) => data.name === serviceName);

                        if (serviceData && serviceData.type) {
                            return [
                                ...prevArray,
                                { name: serviceName, type: serviceData.type },
                            ];
                        }
                    } else {
                        return prevArray.filter(
                            (service) => service.name !== serviceName,
                        );
                    }
                    return prevArray;
                });
            }
        }
    };

    function removeDuplicates(array) {
        const seen = new Set();
        return array.filter((item) => {
            const key = JSON.stringify(item);
            return !seen.has(key) && seen.add(key);
        });
    }

    const handleSubmit = async () => {
        setIsPageLoading(true);

        // An open sub-menu was never saved, so its pending edits do not travel with the
        // parent toggles this button stores.
        const reverted = activeSubmenu ? revertSubmenu(activeSubmenu) : null;

        try {
            // Call onComplete() if both API calls are successful
            if (await persistServices(reverted)) {
                onComplete();
            }
        } catch (error) {
            console.error("Error occurred while updating services:", error);
            toast.error(
                error?.response?.data?.message || "Could not save the services",
            );
        } finally {
            setIsPageLoading(false);
        }
    };

    return (
        <>
            {isPageLoading && <PageLoader />}
            <div className="flex">
                <div className="w-[40%] border-r-[1.5px]">
                    {mainContentData.map((data, index) => (
                        <div
                            className={`flex justify-between border-b-[1.5px] p-2`}
                            key={index}
                        >
                            <div>
                                <p
                                    className={`mb-1 font-semibold ${
                                        data.name === activeSubmenu
                                            ? "text-[#ff7378]"
                                            : ""
                                    }`}
                                    onClick={() =>
                                        handleServiceClick(data.name)
                                    }
                                >
                                    {data.title}
                                </p>
                                <p className="text-[#737880]">
                                    {data.description}
                                </p>
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>
                                <ToggleBtn
                                    onToggle={handleMainToggle}
                                    serviceName={data.name}
                                    serviceTitle={data.name}
                                    initialIsOn={
                                        data.name === "Cab"
                                            ? cabToggle
                                            : data.name ===
                                              "Check In - Check out"
                                            ? checkInOutToggle
                                            : servicesArray.some(
                                                  (service) =>
                                                      service.name ===
                                                      data.name,
                                              )
                                    }
                                />
                            </div>
                        </div>
                    ))}
                    
                    <button
                        className="mt-6 px-4 py-2 bg-[#0B22B2] text-white rounded-md absolute bottom-3 left-[19%]"
                        onClick={handleSubmit}
                    >
                        Next
                    </button>
                </div>
                {activeSubmenu === "HouseKeeping" && (
                    <div className="w-[60%] px-3">
                        <button
                            className="h-[48px] w-full bg-[#ff7378] px-4 rounded-lg text-[16px] mb-3"
                            onClick={handleSaveSubmenu}
                        >
                            Save
                        </button>
                        {houseKeepingData.map((data, index) => (
                            <div
                                className="flex justify-between border-b-[1.5px] p-2"
                                key={index}
                            >
                                <div>
                                    <p className="mb-1 text-[#595E66] font-semibold">
                                        {data.title}
                                    </p>
                                    <p className="text-[#737880]">
                                        {data.description}
                                    </p>
                                </div>
                                <div>
                                    <ToggleBtn
                                        onToggle={handleSubMenuToggle}
                                        serviceName={data.name}
                                        serviceTitle="HouseKeeping"
                                        initialIsOn={
                                            servicesArray.some(
                                                (service) =>
                                                    service.name === data.name,
                                            ) ||
                                            housekeepingArray.some(
                                                (service) =>
                                                    service.name === data.name,
                                            )
                                        }
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeSubmenu === "Cab" && (
                    <div className="w-[60%] px-3">
                        <button
                            className="h-[48px] w-full bg-[#ff7378] px-4 rounded-lg text-[16px] mb-3"
                            onClick={handleSaveSubmenu}
                        >
                            Save
                        </button>
                        {CabData.map((data, index) => (
                            <div
                                className={`p-2 border-b-[1.5px] ${
                                    data.type === "PRE CHECKIN"
                                        ? "bg-[#FFF2F2]"
                                        : ""
                                }`}
                                key={index}
                            >
                                <p className={`text-[#ff7378]`}>{data.type}</p>
                                <div className="flex justify-between mt-3">
                                    <div>
                                        <p className="mb-1 text-[#595E66] font-semibold">
                                            {data.title}
                                        </p>
                                        <p className="text-[#737880]">
                                            {data.description}
                                        </p>
                                    </div>
                                    <div>
                                        <ToggleBtn
                                            onToggle={handleSubMenuToggle}
                                            serviceName={data.name}
                                            serviceTitle="Cab"
                                            initialIsOn={servicesArray.some(
                                                (service) =>
                                                    service.name === data.name,
                                            )}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {activeSubmenu === "Check In - Check out" && (
                    <div className="w-[60%] px-3">
                        <button
                            className="h-[48px] w-full bg-[#ff7378] px-4 rounded-lg text-[16px] mb-3"
                            onClick={handleSaveCheckInOut}
                        >
                            Save
                        </button>
                        {Checkin.map((data, index) => (
                            <div
                                className={`p-2 border-b-[1.5px] ${
                                    data.type === "PRE CHECKIN"
                                        ? "bg-[#FFF2F2]"
                                        : ""
                                }`}
                                key={index}
                            >
                                <p
                                    className={`text-[#ff7378] ${
                                        data.type === Checkin[index - 1]?.type
                                            ? "hidden"
                                            : "block"
                                    } `}
                                >
                                    {data.type}
                                </p>
                                <div className="flex justify-between mt-3">
                                    <div>
                                        <p className="mb-1 text-[#595E66] font-semibold">
                                            {data.title || ""}
                                        </p>
                                        <p className="text-[#737880]">
                                            {data.description}
                                        </p>
                                    </div>
                                    <div>
                                        <ToggleBtn
                                            onToggle={handleSubMenuToggle}
                                            serviceName={data.name}
                                            serviceTitle="Check In - Check out"
                                            initialIsOn={servicesArray.some(
                                                (service) =>
                                                    service.name === data.name,
                                            )}
                                        />
                                    </div>
                                </div>

                                {data.name ===
                                    "PreCheckin Early Checkin Request" &&
                                    isEarlyCheckinEnabled && (
                                        <div className="mt-3 flex gap-3">
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    Hourly Rate (₹)
                                                </p>
                                                <input
                                                    type="text"
                                                    value={earlyCheckinRate}
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            /^\d*$/.test(value)
                                                        ) {
                                                            setEarlyCheckinRate(
                                                                value,
                                                            );
                                                            setEarlyCheckinError(
                                                                "",
                                                            );
                                                        }
                                                    }}
                                                    placeholder="e.g. 500"
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                />
                                            </div>
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    Max Hours
                                                </p>
                                                <input
                                                    type="text"
                                                    value={earlyCheckinMaxHours}
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            /^\d*$/.test(value)
                                                        ) {
                                                            setEarlyCheckinMaxHours(
                                                                value,
                                                            );
                                                            setEarlyCheckinError(
                                                                "",
                                                            );
                                                        }
                                                    }}
                                                    placeholder="e.g. 5"
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                />
                                            </div>
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    GST %
                                                </p>
                                                <select
                                                    value={earlyCheckinGST}
                                                    onChange={(e) => {
                                                        setEarlyCheckinGST(e.target.value);
                                                        setEarlyCheckinError("");
                                                    }}
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                >
                                                    {GST_OPTIONS.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}%
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                {data.name ===
                                    "PreCheckin Early Checkin Request" &&
                                    isEarlyCheckinEnabled &&
                                    earlyCheckinError && (
                                        <p className="text-[#ff7378] text-xs mt-1">
                                            {earlyCheckinError}
                                        </p>
                                    )}

                                {data.name ===
                                    "PostCheckin Late Checkout Request" &&
                                    isLateCheckoutEnabled && (
                                        <div className="mt-3 flex gap-3">
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    Hourly Rate (₹)
                                                </p>
                                                <input
                                                    type="text"
                                                    value={lateCheckoutRate}
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            /^\d*$/.test(value)
                                                        ) {
                                                            setLateCheckoutRate(
                                                                value,
                                                            );
                                                            setLateCheckoutError(
                                                                "",
                                                            );
                                                        }
                                                    }}
                                                    placeholder="e.g. 300"
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                />
                                            </div>
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    Max Hours
                                                </p>
                                                <input
                                                    type="text"
                                                    value={lateCheckoutMaxHours}
                                                    onChange={(e) => {
                                                        const value =
                                                            e.target.value;
                                                        if (
                                                            /^\d*$/.test(value)
                                                        ) {
                                                            setLateCheckoutMaxHours(
                                                                value,
                                                            );
                                                            setLateCheckoutError(
                                                                "",
                                                            );
                                                        }
                                                    }}
                                                    placeholder="e.g. 4"
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                />
                                            </div>
                                            <div className="w-1/3">
                                                <p className="mb-1 text-[#737880] text-sm">
                                                    GST %
                                                </p>
                                                <select
                                                    value={lateCheckoutGST}
                                                    onChange={(e) => {
                                                        setLateCheckoutGST(e.target.value);
                                                        setLateCheckoutError("");
                                                    }}
                                                    className="px-2 py-1.5 rounded-md border-[1.5px] w-full"
                                                >
                                                    {GST_OPTIONS.map((option) => (
                                                        <option key={option} value={option}>
                                                            {option}%
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}

                                {data.name ===
                                    "PostCheckin Late Checkout Request" &&
                                    isLateCheckoutEnabled &&
                                    lateCheckoutError && (
                                        <p className="text-[#ff7378] text-xs mt-1">
                                            {lateCheckoutError}
                                        </p>
                                    )}
                            </div>
                        ))}
                    </div>
                )}
                {activeSubmenu === "In Room Dining" && <Restaurant />}
            </div>
        </>
    );
};

export default Service;
